/**
 * Custom PDF renderer using react-pdf (pdfjs-dist).
 *
 * Replaces the browser-native <iframe> PDF viewer to eliminate Chrome's
 * built-in UI (Google Drive button, print/download chrome). Renders all
 * pages as canvas elements in a scrollable container.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { FileWarning, Loader2, ZoomIn, ZoomOut } from "lucide-react";

// Configure the PDF.js worker — served from dist/ root (copied by build.ts)
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

interface PdfViewerProps {
  /** Blob URL or data URL of the PDF to render. */
  file: string;
  /** Accessible title for the document. */
  title?: string;
}

const ZOOM_STEP = 0.15;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3.0;

export function PdfViewer({ file, title }: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1.0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  // Track the container width so we can set page width to fit
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Use contentBoxSize if available, else clientWidth
        const width = entry.contentBoxSize?.[0]?.inlineSize ?? el.clientWidth;
        setContainerWidth(width);
      }
    });

    observer.observe(el);
    // Set initial width
    setContainerWidth(el.clientWidth);

    return () => observer.disconnect();
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setError(null);
  }, []);

  const onDocumentLoadError = useCallback((err: Error) => {
    console.error("PDF load error:", err);
    setError(err.message || "Failed to load PDF");
  }, []);

  const zoomIn = useCallback(() => {
    setScale((s) => Math.min(s + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((s) => Math.max(s - ZOOM_STEP, MIN_ZOOM));
  }, []);

  const zoomPercent = Math.round(scale * 100);

  // Calculate the effective page width: container width scaled by zoom
  const pageWidth = containerWidth ? containerWidth * scale : undefined;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Zoom controls */}
      <div className="flex items-center justify-center gap-2 px-2 py-1.5 border-b border-border-primary bg-surface-secondary shrink-0">
        <button
          onClick={zoomOut}
          disabled={scale <= MIN_ZOOM}
          className="p-1 rounded hover:bg-interactive-active text-content-secondary hover:text-content-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Zoom out"
        >
          <ZoomOut className="w-3.5 h-3.5" />
        </button>
        <span className="text-xs text-content-tertiary tabular-nums w-10 text-center">{zoomPercent}%</span>
        <button
          onClick={zoomIn}
          disabled={scale >= MAX_ZOOM}
          className="p-1 rounded hover:bg-interactive-active text-content-secondary hover:text-content-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Zoom in"
        >
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Scrollable PDF pages */}
      <div ref={containerRef} className="flex-1 min-h-0 overflow-auto bg-neutral-800">
        <Document
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
              <span className="text-sm text-content-secondary">Loading PDF...</span>
            </div>
          }
          error={
            <div className="flex flex-col items-center justify-center py-16 gap-3 px-4">
              <FileWarning className="w-8 h-8 text-amber-400" />
              <span className="text-sm text-content-secondary text-center">{error ?? "Failed to render PDF"}</span>
            </div>
          }
          externalLinkTarget="_blank"
        >
          {numPages &&
            Array.from({ length: numPages }, (_, i) => (
              <div key={`page_${i + 1}`} className="flex justify-center py-2 first:pt-2">
                <Page
                  pageNumber={i + 1}
                  width={pageWidth}
                  renderAnnotationLayer={true}
                  renderTextLayer={true}
                  loading={
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
                    </div>
                  }
                />
              </div>
            ))}
        </Document>

        {/* Page count footer */}
        {numPages && (
          <div className="text-center py-2 text-xs text-content-quaternary">
            {numPages} page{numPages !== 1 ? "s" : ""}
            {title ? ` — ${title}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}
